import { PaletteOptions } from '@mui/material/styles';
import colorPalettes, { ColorPalettes } from './custom/colorPalettes';
import typography, { TypographySystem } from './custom/typography';
import { TypographyOptions } from '@mui/material/styles/createTypography';
import { extendMuiTheme } from './extendMuiTheme';
import unitMultiplier from './custom/unitMultiplier';
import { ComponentSizes } from './custom/sizeENUM';
import getMfeContainer from '@/utils/mfeContainer';

// Extend MUI theme with custom color palettes and typography
declare module '@mui/material/styles' {
  interface Theme {
    palette: PaletteOptions & ColorPalettes;
    typography: TypographyOptions & TypographySystem;
    unit: Theme['spacing'];
    size: ComponentSizes;
  }

  interface ThemeOptions {
    size?: ComponentSizes;
  }
}

const theme = extendMuiTheme({
  spacing: unitMultiplier,
  palette: {
    primary: {
      main: colorPalettes.M3.sys.light.primary,
      dark: colorPalettes.M3.sys.light['primary-fixed'],
      light: colorPalettes.M3.sys.light['primary-container'],
      contrastText: colorPalettes.M3.sys.light['on-primary'],
    },
    // --- secondary color palette --- need to be updated whenever comes in the design system
    // secondary: {
    //   main: colorPalettes.State.error,
    //   dark: colorPalettes.State['error-pressed'],
    //   light: colorPalettes.State['error-background'],
    //   contrastText: colorPalettes.State['on-error'],
    // },
    error: {
      main: colorPalettes.State.error,
      dark: colorPalettes.State['error-pressed'],
      light: colorPalettes.State['error-background'],
    },
    warning: {
      main: colorPalettes.State.warning,
      dark: colorPalettes.State['warning-pressed'],
      light: colorPalettes.State['warning-background'],
    },
    info: {
      main: colorPalettes.State.info,
      dark: colorPalettes.State['info-pressed'],
      light: colorPalettes.State['info-background'],
    },
    success: {
      main: colorPalettes.State.success,
      dark: colorPalettes.State['success-pressed'],
      light: colorPalettes.State['success-background'],
    },
    background: {
      default: colorPalettes.M3.sys.light.background,
      paper: colorPalettes.M3.sys.light.surface,
    },
    ...colorPalettes,
  },
  typography: {
    // Map M3 typography to standard MUI typography variants
    h1: typography.M3.display.large,
    h2: typography.M3.display.medium,
    h3: typography.M3.display.small,
    h4: typography.M3.headline.large,
    h5: typography.M3.headline.medium,
    h6: typography.M3.headline.small,
    subtitle1: typography.M3.title.large,
    subtitle2: typography.M3.title.medium,
    body1: typography.M3.body.large,
    body2: typography.M3.body.medium,
    button: typography.M3.label.large,
    caption: typography.M3.body.small,
    overline: typography.M3.label.small,
    fontFamily: 'Archivo',
    ...typography,
  },
  // Size configuration - populated by component themes via extendMuiTheme
  // Each component adds its size map when needed (e.g., theme.size.button, theme.size.chip)
  // Components should check for existence before accessing: theme.size?.componentName?.[size]
  size: {},
  components: {
    MuiPopover: {
      defaultProps: { container: getMfeContainer, sx: { zIndex: 1500 } },
    },
    MuiModal: {
      defaultProps: { container: getMfeContainer, sx: { zIndex: 1500 } },
    },
    MuiDialog: {
      defaultProps: { container: getMfeContainer, sx: { zIndex: 1500 } },
    },
    MuiMenu: {
      defaultProps: { sx: { zIndex: 1500 } },
    },
    MuiBackdrop: {
      defaultProps: { sx: { zIndex: 1400 } },
    },
  },
});

// Assign unit as an alias for spacing
theme.unit = theme.spacing;

export { theme };
